import type { Product, DeliveryZone, CreateOrderPayload, Order, OrderStatus, PaymentStatus } from "@cieda/types";
import { getMockData } from "./mock-storefront-data";
import {
  RateLimitError,
  ApiError,
  NetworkError,
  showRateLimitToast,
} from "./api-error-handler";
import { supabase } from "./supabase";

// Re-export error classes so consumers can import from a single place
export { RateLimitError, ApiError, NetworkError } from "./api-error-handler";

// In dev, use empty string so requests go through Vite's proxy (/api → localhost:3001).
// In production, VITE_API_URL must be set to the real API origin.
const API_URL = import.meta.env.VITE_API_URL || "";

/**
 * Parse the Retry-After header value.
 * Supports both delta-seconds ("60") and HTTP-date formats.
 * Falls back to `fallback` when the header is missing or unparseable.
 */
function parseRetryAfter(header: string | null, fallback = 30): number {
  if (!header) return fallback;
  const seconds = Number(header);
  if (!Number.isNaN(seconds) && seconds > 0) return Math.ceil(seconds);
  // HTTP-date format
  const date = Date.parse(header);
  if (!Number.isNaN(date)) {
    const delta = Math.ceil((date - Date.now()) / 1_000);
    return delta > 0 ? delta : fallback;
  }
  return fallback;
}

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  // Attach Supabase auth token if user is logged in
  const authHeaders: Record<string, string> = {};
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.access_token) {
      authHeaders["Authorization"] = `Bearer ${session.access_token}`;
    }
  } catch {
    // Auth is best-effort — guest checkout still works
  }

  let res: Response;
  try {
    res = await fetch(`${API_URL}${path}`, {
      ...options,
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        ...authHeaders,
        ...options?.headers,
      },
    });
  } catch {
    // Network-level failure (offline, DNS, CORS)
    if (import.meta.env.DEV) {
      const mock = getMockData(path);
      if (mock !== undefined) {
        console.info(`[MOCK] ${path} — using demo data`);
        return mock as T;
      }
    }
    throw new NetworkError();
  }

  if (res.ok) {
    return await res.json();
  }

  // ---- Non-2xx handling ----

  // 429 — Rate limited
  if (res.status === 429) {
    const retryAfter = parseRetryAfter(res.headers.get("Retry-After"));
    showRateLimitToast(retryAfter);
    throw new RateLimitError(retryAfter);
  }

  // 401 — Unauthorized: redirect to login (avoid loops)
  if (res.status === 401) {
    if (!window.location.pathname.startsWith("/connexion")) {
      window.location.href = "/connexion";
    }
    throw new ApiError(401, "Session expirée");
  }

  // Other errors — parse body if possible
  const body = await res.json().catch(() => ({ error: "Erreur réseau" }));
  const message: string = body?.error ?? `HTTP ${res.status}`;

  // Dev mock fallback for non-critical errors
  if (import.meta.env.DEV) {
    const mock = getMockData(path);
    if (mock !== undefined) {
      console.info(`[MOCK] ${path} — using demo data`);
      return mock as T;
    }
  }

  throw new ApiError(res.status, message);
}

export const api = {
  catalog: {
    products: (params?: Record<string, string>) => {
      const qs = params ? "?" + new URLSearchParams(params).toString() : "";
      return request<{
        products: Product[];
        pagination: { page: number; limit: number; total: number };
      }>(`/api/catalog/products${qs}`);
    },
    product: (id: string) =>
      request<Product>(`/api/catalog/products/${id}`),
    deliveryZones: (regionId?: string) => {
      const qs = regionId ? `?region_id=${regionId}` : "";
      return request<DeliveryZone[]>(`/api/catalog/delivery-zones${qs}`);
    },
  },
  orders: {
    listMine: (params?: { limit?: number; cursor?: string }) => {
      const qs = new URLSearchParams();
      if (params?.limit) qs.set("limit", String(params.limit));
      if (params?.cursor) qs.set("cursor", params.cursor);
      const query = qs.toString();
      return request<{
        orders: Array<{ ref: string; date: string; status: OrderStatus; total: number }>;
        nextCursor: string | null;
        hasMore: boolean;
        total?: number;
      }>(`/api/orders/mine${query ? `?${query}` : ""}`);
    },
    create: (payload: CreateOrderPayload) =>
      request<{
        order: { id: string; public_ref: string; status: string; total_ttc_fcfa: number; payment_method: string };
        payment_id: string | null;
        tracking_url: string;
      }>("/api/orders", {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    // Phase-3 bug: the tracking page was returning "Commande introuvable"
    // for real orders because the token was never forwarded. The backend
    // HMAC-validates `?token=...` on every tracking lookup — without it,
    // the endpoint returns 404 as an anti-enumeration measure. The token
    // comes from the `tracking_url` the API returns on order creation.
    track: (ref: string, token?: string | null) => {
      const qs = token ? `?token=${encodeURIComponent(token)}` : "";
      return request<{ order: Order; items: unknown[]; delivery: unknown }>(
        `/api/orders/track/${encodeURIComponent(ref)}${qs}`,
      );
    },
    detail: (ref: string) =>
      request<{ order: Record<string, unknown> }>(`/api/orders/mine/${ref}`),
  },
  payments: {
    // token is required by the backend since the tracking-token auth change.
    // It is extracted from the tracking_url returned by api.orders.create.
    status: (id: string, token: string) => {
      const qs = `?token=${encodeURIComponent(token)}`;
      return request<{ status: PaymentStatus }>(`/api/payments/${id}/status${qs}`);
    },
  },
  delivery: {
    zones: () =>
      request<{
        regions: Array<{
          id: string;
          name: string;
          code: string;
          active: boolean;
          zones: Array<{
            id: string;
            zone_name: string;
            fee_own_car_fcfa: number;
            fee_own_bike_fcfa: number | null;
            fee_yango_estimated_fcfa: number | null;
            estimated_time_minutes: number;
            centroid_lat: number | null;
            centroid_lng: number | null;
          }>;
        }>;
      }>("/api/delivery/zones"),
    config: () =>
      request<{
        delivery_mode: string;
        service_fee: { type: string; value: number };
        free_delivery_threshold_fcfa: number;
        default_method_id: string | null;
      }>("/api/delivery/config"),
    methods: () =>
      request<{
        methods: Array<{
          id: string;
          name: string;
          type: string;
          config: Record<string, unknown>;
        }>;
      }>("/api/delivery/methods"),
  },
  addresses: {
    list: () =>
      request<{ addresses: Array<{ id: string; label: string; full_address: string; quartier: string; commune: string; region_id: string; lat: number | null; lng: number | null; phone_contact: string; is_default: boolean; created_at: string }> }>("/api/addresses"),
    create: (data: { label: string; full_address: string; quartier: string; commune: string; region_id: string; phone_contact: string; lat?: number; lng?: number; is_default?: boolean }) =>
      request<{ address: Record<string, unknown> }>("/api/addresses", { method: "POST", body: JSON.stringify(data) }),
    update: (id: string, data: Record<string, unknown>) =>
      request<{ address: Record<string, unknown> }>(`/api/addresses/${id}`, { method: "PUT", body: JSON.stringify(data) }),
    delete: (id: string) =>
      request<{ success: boolean }>(`/api/addresses/${id}`, { method: "DELETE" }),
  },
  wishlist: {
    list: () =>
      request<{ items: Array<{ id: string; product: Product; added_at: string }> }>("/api/wishlist"),
    add: (productId: string) =>
      request<{ item: { id: string; product_id: string; added_at: string } }>("/api/wishlist", { method: "POST", body: JSON.stringify({ product_id: productId }) }),
    remove: (productId: string) =>
      request<{ success: boolean }>(`/api/wishlist?product_id=${productId}`, { method: "DELETE" }),
    share: () =>
      request<{ shareToken: string; shareUrl: string }>("/api/wishlist/share", { method: "POST" }),
    revokeShare: () =>
      request<{ success: boolean }>("/api/wishlist/share", { method: "DELETE" }),
    getShared: (token: string) =>
      request<{
        title: string;
        items: Array<{ product_id: string; name: string; price_fcfa: number; image_url: string | null }>;
        ownerName: string;
        createdAt: string;
      }>(`/api/public/wishlist/${token}`),
  },
  profile: {
    get: () =>
      request<{
        profile: {
          full_name: string;
          email: string;
          phone: string;
          preferred_language: "fr" | "wo" | "en";
          created_at: string;
        };
      }>("/api/account/profile"),
    update: (data: {
      full_name?: string;
      phone?: string | null;
      preferred_language?: "fr" | "wo" | "en";
    }) =>
      request<{
        profile: {
          full_name: string;
          email: string;
          phone: string;
          preferred_language: "fr" | "wo" | "en";
        };
      }>("/api/account/profile", {
        method: "PATCH",
        body: JSON.stringify(data),
      }),
  },
  account: {
    linkOrder: (orderRef: string) =>
      request<{ linked: boolean }>("/api/account/link-order", {
        method: "POST",
        body: JSON.stringify({ order_ref: orderRef }),
      }),
    getNotificationPrefs: () =>
      request<{ preferences: { email: boolean; sms: boolean; whatsapp: boolean } }>("/api/account/notifications"),
    updateNotificationPrefs: (prefs: { email?: boolean; sms?: boolean; whatsapp?: boolean }) =>
      request<{ preferences: { email: boolean; sms: boolean; whatsapp: boolean } }>("/api/account/notifications", { method: "PUT", body: JSON.stringify(prefs) }),
    requestDeletion: (reason?: string) =>
      request<{ request_id: string; status: string; message: string }>("/api/account", { method: "DELETE", body: JSON.stringify({ reason }) }),
    exportData: async (): Promise<Blob> => {
      const res = await fetch(`${API_URL}/api/account`, { credentials: "include", headers: { "Content-Type": "application/json" } });
      if (!res.ok) throw new Error("Export failed");
      return res.blob();
    },
  },
  cms: {
    heroSlides: () =>
      request<{
        slides: Array<{
          id: string;
          title: string;
          title_highlight: string | null;
          badge: string | null;
          subtitle: string | null;
          image_url: string;
          cta_text: string | null;
          cta_link: string | null;
          sort_order: number;
        }>;
      }>("/api/cms/hero-slides"),
    brandShowcases: () =>
      request<{
        brands: Array<{
          id: string;
          brand_name: string;
          slug: string;
          tagline: string | null;
          description: string | null;
          logo_url: string | null;
          cover_image_url: string | null;
          accent_color: string;
          sort_order: number;
        }>;
      }>("/api/cms/brand-showcases"),
    activePromo: () =>
      request<{
        promo: {
          id: string;
          title: string;
          subtitle: string | null;
          cta_text: string | null;
          cta_link: string | null;
          bg_image_url: string | null;
        } | null;
      }>("/api/cms/active-promo"),

    sectionSettings: () =>
      request<{
        sections: Array<{
          id: string;
          section_key: string;
          label: string;
          enabled: boolean;
          sort_order: number;
        }>;
      }>("/api/cms/section-settings"),

    announcements: () =>
      request<{
        announcements: Array<{
          id: string;
          text: string;
          link: string | null;
          bg_color: string;
          text_color: string;
          dismissible: boolean;
          sort_order: number;
        }>;
      }>("/api/cms/announcements"),

    categories: () =>
      request<{
        categories: Array<{
          id: string;
          label: string;
          icon_name: string;
          slug: string;
          image_url: string | null;
          sort_order: number;
        }>;
      }>("/api/cms/categories"),

    flashDeals: () =>
      request<{
        deal: {
          id: string;
          title: string;
          ends_at: string;
          discount_percent: number | null;
          product_ids: string[];
        } | null;
      }>("/api/cms/flash-deals"),

    featured: (type?: string) => {
      const qs = type ? `?type=${type}` : "";
      return request<{
        collections: Array<{
          id: string;
          title: string;
          subtitle: string | null;
          type: string;
          product_ids: string[];
          layout: string;
          sort_order: number;
        }>;
      }>(`/api/cms/featured${qs}`);
    },

    promoBanners: (position?: string) => {
      const qs = position ? `?position=${position}` : "";
      return request<{
        banners: Array<{
          id: string;
          title: string;
          subtitle: string | null;
          image_url: string;
          mobile_image_url: string | null;
          cta_text: string | null;
          cta_link: string | null;
          position: string;
          sort_order: number;
        }>;
      }>(`/api/cms/promo-banners${qs}`);
    },

    trustItems: () =>
      request<{
        items: Array<{
          id: string;
          icon_name: string;
          title: string;
          description: string | null;
          sort_order: number;
        }>;
      }>("/api/cms/trust-items"),

    socialProof: () =>
      request<{
        items: Array<{
          id: string;
          type: string;
          content: string;
          author: string | null;
          rating: number | null;
          sort_order: number;
        }>;
      }>("/api/cms/social-proof"),

    appBanner: () =>
      request<{
        banner: {
          id: string;
          title: string;
          subtitle: string | null;
          ios_link: string | null;
          android_link: string | null;
          image_url: string | null;
        } | null;
      }>("/api/cms/app-banner"),

    seo: (page: string) =>
      request<{
        meta: {
          title: string;
          description: string | null;
          og_image_url: string | null;
        } | null;
      }>(`/api/cms/seo/${page}`),
  },
  settings: {
    checkout: () =>
      request<{
        whatsapp_enabled: boolean;
        whatsapp_number: string;
        wave_number: string;
        orange_number: string;
      }>("/api/settings/checkout"),
  },
};
